import { RH } from '@/constants/permissions'
import { userHasPermission } from '@/shared/lib/roleUtils'

/**
 * Cobertura de la vacante y confirmación del interno (SRH-313).
 *
 * Una plaza la ocupa un externo seleccionado o un interno que RH confirmó. Al
 * cubrirse todas, la vacante se cierra sola, y los candidatos que seguían en
 * proceso quedan marcados «Vacante cubierta» para que RH decida.
 */

export const ETIQUETA_VACANTE_CUBIERTA = 'Vacante cubierta'

/** Los estatus de una vacante en los que RH puede confirmar al interno elegido. */
const ESTATUS_PARA_CONFIRMAR = ['vacante_activa', 'cubierta']

export interface InternoGuardado {
  name: string
  currentPosition: string
  isSelected?: boolean
  /** Cuándo lo confirmó RH; nulo mientras solo lo eligió el solicitante. */
  confirmedAt?: string | null
}

export type EstadoDelInterno = 'sin_elegir' | 'por_confirmar' | 'confirmado'

/** El interno que eligió el solicitante, si lo hay. */
export const internoElegido = (
  internos: InternoGuardado[] | undefined,
): InternoGuardado | undefined => (internos ?? []).find((i) => i.isSelected === true)

export const estadoDelInterno = (
  internos: InternoGuardado[] | undefined,
): EstadoDelInterno => {
  const elegido = internoElegido(internos)
  if (!elegido) return 'sin_elegir'
  return elegido.confirmedAt ? 'confirmado' : 'por_confirmar'
}

/**
 * Quién puede confirmar: quien cierra la selección, con la vacante activa o ya
 * cubierta. En una cubierta es sobrecupo y el servidor advierte; en otra
 * etapa no tiene sentido ofrecerlo.
 */
export const puedeConfirmarInterno = (
  permisos: string[] | undefined,
  estatusDeLaVacante: string | undefined,
): boolean =>
  userHasPermission(RH.RECLUTAMIENTO_CERRAR_SELECCION, permisos ?? []) &&
  !!estatusDeLaVacante &&
  ESTATUS_PARA_CONFIRMAR.includes(estatusDeLaVacante)

/**
 * Si el error del servidor es el aviso de sobrecupo, que se confirma y se
 * reintenta, y no un fallo. El servidor lo responde como 409 con este texto.
 */
export const esAvisoDeSobrecupo = (error: unknown): boolean => {
  const e = error as { statusCode?: number; status?: number; message?: string }
  const codigo = e?.statusCode ?? e?.status
  return codigo === 409 && /plazas cubiertas/i.test(e?.message ?? '')
}

/**
 * Si el candidato sigue en proceso en una vacante que ya se cubrió.
 *
 * Los que ya se cerraron —seleccionados, contratados— o se descartaron no
 * llevan la marca: ya tuvieron su resultado.
 */
const ESTATUS_SIN_MARCA = [
  'seleccionado',
  'contratado',
  'rechazado',
  'rechazado_evaluacion_rh',
]

export const vacanteCubiertaParaElCandidato = (candidato: {
  status?: string | null
  jobRequest?: { status?: string | null } | null
}): boolean =>
  candidato.jobRequest?.status === 'cubierta' &&
  !!candidato.status &&
  !ESTATUS_SIN_MARCA.includes(candidato.status)

/** Los ids sin repetir, para pedir los candidatos de las vacantes activas y las cubiertas. */
export const idsDeVacantes = (...listas: Array<Array<{ id: number }>>): number[] => [
  ...new Set(listas.flat().map((v) => v.id)),
]
